import {
  CREST_SHAPING,
  INLAND_SPECTRUM_SLOTS,
  INLAND_WAVELENGTH_MIN_PX,
  JUDDER_HULL_FRACTION,
  JUDDER_MAX_PX,
  JUDDER_ROTATION_PER_PX,
  SIGNIFICANT_HEIGHT_MIN_PX,
  SPECTRUM_PHASES,
  SPECTRUM_SLOTS,
  STEEPNESS_EXAGGERATION,
  WAVELENGTH_MIN_PX,
} from './constants';
import { lerp } from './math';

/** One sine component of the sea: amplitude, screen wavenumber, real frequency. */
interface SpectrumComponent {
  amplitude: number;
  wavenumber: number;
  frequency: number;
}

interface BuildInputs {
  /** Swell significant height, metres. Zero on inland water. */
  swellHeightM: number;
  /** Swell peak period, seconds. Zero on inland water. */
  swellPeriodS: number;
  /** Wind-sea significant height, metres. */
  windSeaHeightM: number;
  /** Wind-sea period, seconds. */
  windSeaPeriodS: number;
  /** The boat's own scale, hull px per metre, so the sea draws to scale. */
  pxPerM: number;
  /** Canvas width, px. */
  width: number;
  /** Canvas height, px. */
  height: number;
  /** Wave draw cap, px. */
  clampPx: number;
  /** 0 keeps the original narrow inland spectrum; 1 uses the full chop spread. */
  inlandChop: number;
}

const isSameInputs = (a: BuildInputs, b: BuildInputs): boolean =>
  a.swellHeightM === b.swellHeightM &&
  a.swellPeriodS === b.swellPeriodS &&
  a.windSeaHeightM === b.windSeaHeightM &&
  a.windSeaPeriodS === b.windSeaPeriodS &&
  a.pxPerM === b.pxPerM &&
  a.width === b.width &&
  a.height === b.height &&
  a.clampPx === b.clampPx &&
  a.inlandChop === b.inlandChop;

interface JudderOffsets {
  yPx: number;
  rotationRad: number;
}

/**
 * The sea surface: a sum of sine components split between a swell band and a
 * wind-sea band.
 *
 * The sea is defined directly in screen pixels, with visual wavelengths floored
 * so chop reads as ripples rather than spikes. Time stays in real seconds, so
 * wave timing — and therefore hull resonance — is physically real.
 *
 * Wave geometry is rendered at the *boat's* own pixels-per-metre, so wave height
 * is always to scale against whatever vessel is shown. Wavelength follows the
 * deep-water law L = 1.56T², compressed by a constant
 * (`STEEPNESS_EXAGGERATION`) so steepness is uniformly exaggerated for
 * legibility but stays correctly ordered.
 */
export class SeaSpectrum {
  private readonly components: SpectrumComponent[] = SPECTRUM_SLOTS.map(() => ({
    amplitude: 0,
    wavenumber: 0,
    frequency: 0,
  }));

  private readonly phases: number[] = [...SPECTRUM_PHASES];

  /** Reused blended inland slots; rebuilt only when the dirty inputs change. */
  private readonly inlandSlots: Array<{ mult: number; weight: number }> =
    SPECTRUM_SLOTS.map((slot) => ({
      mult: slot.mult,
      weight: slot.weight,
    }));

  /**
   * Current significant height in px. `elevationAt` normalises its crest
   * shaping against this.
   */
  private significantPx = 8;

  /**
   * Dirty flag for `build`. The inputs only change on a scrub, a day tap, a
   * vessel switch or a resize, so the rebuild is skipped on every other frame.
   */
  private lastInputs: BuildInputs | null = null;

  significantHeightPx(): number {
    return this.significantPx;
  }

  /**
   * Rebuild the components for the current sea. Cheap to call every frame: it
   * returns immediately when nothing has moved.
   */
  build(inputs: BuildInputs): void {
    if (this.lastInputs != null && isSameInputs(this.lastInputs, inputs)) {
      return;
    }

    this.lastInputs = { ...inputs };

    const {
      swellHeightM,
      swellPeriodS,
      windSeaHeightM,
      windSeaPeriodS,
      pxPerM,
      width,
      clampPx,
      inlandChop,
    } = inputs;

    const inland = swellHeightM <= 0.0001;
    const wavelengthMaxPx = Math.max(180, width * 0.95);
    const wavelengthMinPx = inland
      ? lerp(WAVELENGTH_MIN_PX, INLAND_WAVELENGTH_MIN_PX, inlandChop)
      : WAVELENGTH_MIN_PX;
    const wavelengthPx = (periodS: number): number =>
      Math.max(
        wavelengthMinPx,
        Math.min(
          wavelengthMaxPx,
          ((1.56 * periodS * periodS) / STEEPNESS_EXAGGERATION) * pxPerM,
        ),
      );

    const swellVariance = swellHeightM * swellHeightM;
    const windVariance = windSeaHeightM * windSeaHeightM;

    if (inland) {
      for (let i = 0; i < this.inlandSlots.length; i += 1) {
        this.inlandSlots[i].mult = lerp(
          SPECTRUM_SLOTS[i].mult,
          INLAND_SPECTRUM_SLOTS[i].mult,
          inlandChop,
        );
        this.inlandSlots[i].weight = lerp(
          SPECTRUM_SLOTS[i].weight,
          INLAND_SPECTRUM_SLOTS[i].weight,
          inlandChop,
        );
      }
    }

    let varianceSum = 0;

    for (let i = 0; i < SPECTRUM_SLOTS.length; i += 1) {
      const slot = inland ? this.inlandSlots[i] : SPECTRUM_SLOTS[i];
      // With no swell in the feed, every slot carries wind sea instead.
      const useWind = inland || SPECTRUM_SLOTS[i].kind === 'wind';
      const periodS = Math.max(
        (useWind ? windSeaPeriodS : swellPeriodS) * slot.mult,
        0.4,
      );

      // Relative energy splits the variance between the two bands.
      const energy =
        slot.weight * (useWind ? (inland ? 1 : windVariance) : swellVariance);

      const component = this.components[i];
      component.amplitude = Math.sqrt(Math.max(energy, 0));
      component.frequency = (2 * Math.PI) / periodS;
      component.wavenumber = (2 * Math.PI) / wavelengthPx(periodS);

      varianceSum += (component.amplitude * component.amplitude) / 2;
    }

    // Significant height in px at the boat's scale; swell and wind sea combine
    // in quadrature just as they do in the rating.
    const significantPx = Math.max(
      SIGNIFICANT_HEIGHT_MIN_PX,
      Math.min(
        clampPx * 0.98,
        Math.sqrt(swellVariance + windVariance) * pxPerM,
      ),
    );

    this.significantPx = significantPx;

    const scale = Math.sqrt(
      (significantPx / 4) ** 2 / Math.max(varianceSum, 1e-6),
    );

    this.components.forEach((component) => {
      // eslint-disable-next-line no-param-reassign
      component.amplitude *= scale;
    });
  }

  /** Advance every component's phase by `dt` seconds. */
  advance(dt: number): void {
    for (let i = 0; i < this.components.length; i += 1) {
      this.phases[i] += this.components[i].frequency * dt;
    }
  }

  /**
   * Quick irregular draw-time shudder driven by the live spectrum phases. The
   * caller supplies the residual-derived chop level and decides when the effect
   * is allowed (inland, upright, animated).
   */
  judderOffsets(chop: number, hullPx: number): JudderOffsets {
    if (chop <= 0) {
      return { yPx: 0, rotationRad: 0 };
    }

    const amplitude =
      chop * Math.min(JUDDER_MAX_PX, hullPx * JUDDER_HULL_FRACTION);
    const t1 = this.phases[0] * 2.6;
    const t2 = this.phases[5] * 4.6;

    return {
      yPx: amplitude * (0.68 * Math.sin(t1) + 0.32 * Math.sin(t2 * 1.37 + 1.1)),
      rotationRad:
        amplitude *
        JUDDER_ROTATION_PER_PX *
        (0.6 * Math.sin(t2) + 0.4 * Math.sin(t1 * 1.61 + 2.3)),
    };
  }

  /**
   * Surface elevation in px (positive up) at screen x.
   *
   * The raw sine sum is symmetric, which is the big synthetic tell — real waves
   * carry peaked crests and flat troughs. A Stokes-like quadratic term pushes
   * crests higher and makes troughs shallower with the mean preserved
   * (`s² × 0.0625` is the expected value of e², i.e. `(significantPx / 4)²`).
   * One extra multiply per sample, and the hull physics rides the shaped surface
   * too, so pitch and heave stay consistent with what is drawn.
   */
  elevationAt(x: number): number {
    let e = 0;

    for (let i = 0; i < this.components.length; i += 1) {
      e +=
        this.components[i].amplitude *
        Math.sin(this.components[i].wavenumber * x + this.phases[i]);
    }

    const s = Math.max(this.significantPx, 4);

    return e + (CREST_SHAPING * (e * e - s * s * 0.0625)) / s;
  }
}
